import { defineMessages } from 'react-intl';

import type { OrderAppError } from '@/graphql/orderApp/api';
import type { CheckoutError } from '@/graphql/saleor/api';
import { INTL_PREFIX } from '@/messages/const';
import { formMessages } from '@/messages/forms';

import { commonErrorCodeMessages, type FormattedError } from './common';

const intlPrefix = INTL_PREFIX.CHECKOUT;

export const errors = defineMessages({
  notFound: {
    id: `${intlPrefix}.notFound`,
    defaultMessage: 'Checkout does not exist.',
  },
  missingDeliveryMethod: {
    id: `${intlPrefix}.missingDeliveryMethod`,
    defaultMessage: 'There is no applicable delivery method for this checkout.',
  },
  invalidPhone: {
    id: `${intlPrefix}.invalidPhone`,
    defaultMessage: 'Invalid phone number.',
  },
  invalidPostalCode: {
    id: `${intlPrefix}.invalidPostalCode`,
    defaultMessage: 'Invalid postal code.',
  },
  invalidCountryArea: {
    id: `${intlPrefix}.invalidCountryArea`,
    defaultMessage: 'Invalid country area.',
  },
  invalidPartialAmount: {
    id: `${intlPrefix}.invalidPartialAmount`,
    defaultMessage: 'Payment amount should be greater than 20% of rest amount',
  },
  invalidEmail: {
    id: `${intlPrefix}.invalidEmail`,
    defaultMessage: 'Invalid email address.',
  },
  shippingAddressBlackList: {
    id: `checkout.shippingAddressBlackList`,
    defaultMessage:
      'We are currently unable to deliver to this area. Please choose another delivery destination or contact our customer relations squad',
  },
  shippingAddressPoBoxNotSupported: {
    id: `checkout.shippingAddressPoBoxNotSupported`,
    defaultMessage:
      'We are currently unable to deliver to P.O. Box addresses. Please choose another delivery destination or contact our customer relations squad',
  },
  shippingAddressParcelLockerNotSupported: {
    id: `checkout.shippingAddressParcelLockerNotSupported`,
    defaultMessage:
      'We are currently unable to deliver to Parcel Locker addresses. Please choose another delivery destination or contact our customer relations squad',
  },
  shippingAddressHotelNotSupported: {
    id: `checkout.shippingAddressHotelNotSupported`,
    defaultMessage:
      'We are currently unable to deliver to Hotel addresses. Please choose another delivery destination or contact our customer relations squad',
  },
  shippingAddressMilitaryBaseNotSupported: {
    id: `checkout.shippingAddressMilitaryBaseNotSupported`,
    defaultMessage:
      'We are currently unable to deliver to Military Base addresses. Please choose another delivery destination or contact our customer relations squad',
  },
  checkoutIdExpired: {
    id: `${intlPrefix}.checkoutIdExpired`,
    defaultMessage: 'Checkout has expired',
  },
});

export const getCheckoutErrorCodeMessage = (
  error: FormattedError<CheckoutError | OrderAppError>,
) => {
  switch (error.code) {
    case 'EXPIRED':
      return errors.checkoutIdExpired;

    case 'NOT_FOUND':
      return errors.notFound;

    case 'DELIVERY_METHOD_NOT_APPLICABLE':
      return errors.missingDeliveryMethod;

    case 'REQUIRED':
      return formMessages.requiredField;

    case 'shipping_address.blacklisted':
      return errors.shippingAddressBlackList;

    case 'shipping_address.po_box_not_supported':
      return errors.shippingAddressPoBoxNotSupported;

    case 'shipping_address.parcel_locker_not_supported':
      return errors.shippingAddressParcelLockerNotSupported;

    case 'shipping_address.hotel_not_supported':
      return errors.shippingAddressHotelNotSupported;

    case 'shipping_address.military_base_not_supported':
      return errors.shippingAddressMilitaryBaseNotSupported;

    case 'INVALID':
    case 'saleor_validation_error.INVALID':
      switch (error.field) {
        case 'phone':
          return errors.invalidPhone;
        case 'postalCode':
          return errors.invalidPostalCode;
        case 'countryArea':
          return errors.invalidCountryArea;
        case 'email':
          return errors.invalidEmail;
      }

    // eslint-disable-next-line no-fallthrough
    default:
      return error.message
        ? error.message
        : commonErrorCodeMessages.genericCheckoutError;
  }
};
